<template>
  <div :class="$style.root">
    <button
      v-if="hasContent"
      ref="trigger"
      :class="$style.trigger"
      type="button"
      data-content-journey-trigger
      :aria-label="
        localizedStrings['content-pathing-flyout-open'] ||
        'Open content pathing'
      "
      :aria-controls="dialogId"
      :aria-expanded="isOpen"
      @click="open"
    >
      <svg
        :class="$style.triggerIcon"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M20 19L20.5303 19.5303C20.8232 19.2374 20.8232 18.7626 20.5303 18.4697L20 19ZM8 4.25C7.58579 4.25 7.25 4.58579 7.25 5C7.25 5.41421 7.58579 5.75 8 5.75V4.25ZM18.5303 16.4697C18.2374 16.1768 17.7626 16.1768 17.4697 16.4697C17.1768 16.7626 17.1768 17.2373 17.4697 17.5303L18.5303 16.4697ZM17.4697 20.4697C17.1768 20.7626 17.1768 21.2373 17.4697 21.5303C17.7626 21.8232 18.2374 21.8232 18.5303 21.5303L17.4697 20.4697ZM13.75 18.25C13.3358 18.25 13 18.5858 13 19C13 19.4142 13.3358 19.75 13.75 19.75V18.25ZM10 19.75C10.4142 19.75 10.75 19.4142 10.75 19C10.75 18.5858 10.4142 18.25 10 18.25V19.75ZM11 5.75C11.4142 5.75 11.75 5.4142 11.75 5C11.75 4.5858 11.4142 4.25 11 4.25V5.75ZM15 4.25C14.5858 4.25 14.25 4.5858 14.25 5C14.25 5.4142 14.5858 5.75 15 5.75V4.25ZM7.5 12.75H16.5V11.25H7.5V12.75ZM20.5303 18.4697L18.5303 16.4697L17.4697 17.5303L19.4697 19.5303L20.5303 18.4697ZM19.4697 18.4697L17.4697 20.4697L18.5303 21.5303L20.5303 19.5303L19.4697 18.4697ZM20.75 8.5C20.75 6.1528 18.8472 4.25 16.5 4.25V5.75C18.0188 5.75 19.25 6.9812 19.25 8.5H20.75ZM16.5 12.75C18.8472 12.75 20.75 10.8472 20.75 8.5H19.25C19.25 10.0188 18.0188 11.25 16.5 11.25V12.75ZM4.75 15.5C4.75 13.9812 5.9812 12.75 7.5 12.75V11.25C5.1528 11.25 3.25 13.1528 3.25 15.5H4.75ZM7.5 18.25C5.9812 18.25 4.75 17.0188 4.75 15.5H3.25C3.25 17.8472 5.1528 19.75 7.5 19.75V18.25ZM20 18.25H13.75V19.75H20V18.25ZM10 18.25H7.5V19.75H10V18.25ZM11 4.25H8V5.75H11V4.25ZM16.5 4.25H15V5.75H16.5V4.25Z"
          fill="currentColor"
        />
        <circle cx="6" cy="5" r="2" stroke="currentColor" stroke-width="1.5" />
      </svg>
      <span :class="$style.visuallyHidden">
        {{ localizedStrings['content-pathing'] || 'Content pathing' }}
      </span>
    </button>

    <Teleport to="body">
      <div
        v-if="hasContent"
        :class="[$style.portal, isOpen && $style.portalOpen]"
        data-content-journey-portal
        :data-open="isOpen"
        :data-interactive="isOpen"
        @keydown.esc.stop.prevent="close"
      >
        <div
          v-if="isOpen"
          :class="$style.backdrop"
          data-content-journey-backdrop
          @click="close"
        />
        <Transition
          :enter-active-class="$style.drawerEnterActive"
          :enter-from-class="$style.drawerEnterFrom"
          :enter-to-class="$style.drawerEnterTo"
          :leave-active-class="$style.drawerLeaveActive"
          :leave-from-class="$style.drawerLeaveFrom"
          :leave-to-class="$style.drawerLeaveTo"
        >
          <aside
            v-if="isOpen"
            :id="dialogId"
            ref="dialog"
            :class="$style.drawer"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
            tabindex="-1"
            @click.stop="handleDrawerClick"
            @keydown.tab="trapFocus"
          >
            <header :class="$style.header">
              <h2 :id="titleId" :class="$style.heading">
                {{ localizedStrings['content-pathing'] || 'Content Pathing' }}
              </h2>
              <button
                ref="closeButton"
                :class="$style.closeButton"
                type="button"
                :aria-label="
                  localizedStrings['content-pathing-flyout-close'] ||
                  'Close content pathing'
                "
                @click="close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="m6 6 12 12M18 6 6 18"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
            </header>

            <div :class="$style.body">
              <ContentJourney
                v-if="loading && !journeys.length"
                :loading="true"
              />
              <ContentJourney
                v-for="journey in journeys"
                :key="journey.id"
                :journey="journey"
                :initially-expanded="
                  !hasKnownActiveJourney || journey.id === props.activeJourneyId
                "
                layout="drawer"
                :class="$style.journey"
              />
            </div>
          </aside>
        </Transition>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import ContentJourney from '~/components/ContentJourney.vue'
import type { ContentJourneyData } from '~/services/contentJourney'
import {
  acquireBodyScrollLock,
  releaseBodyScrollLock,
} from '~/utils/bodyScrollLock'

const { localizedStrings } = useLocalizedStrings()

const props = withDefaults(
  defineProps<{
    journeys?: ContentJourneyData[]
    loading?: boolean
    activeJourneyId?: string
  }>(),
  {
    journeys: () => [],
    loading: false,
    activeJourneyId: '',
  },
)

const dialogId = 'content-journey-flyout'
const titleId = 'content-journey-flyout-title'
const isOpen = ref(false)
const trigger = ref<HTMLButtonElement | null>(null)
const dialog = ref<HTMLElement | null>(null)
const closeButton = ref<HTMLButtonElement | null>(null)
const previousActiveElement = ref<HTMLElement | null>(null)
const bodyLocked = ref(false)

const hasContent = computed(() => props.loading || props.journeys.length > 0)

const hasKnownActiveJourney = computed(
  () =>
    Boolean(props.activeJourneyId) &&
    props.journeys.some((journey) => journey.id === props.activeJourneyId),
)

const focusableSelector =
  'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'

const open = async () => {
  if (!hasContent.value) return

  previousActiveElement.value = document.activeElement as HTMLElement
  if (!bodyLocked.value) {
    acquireBodyScrollLock()
    bodyLocked.value = true
  }
  isOpen.value = true

  await nextTick()
  closeButton.value?.focus()
}

const close = () => {
  isOpen.value = false
}

const handleDrawerClick = (event: MouseEvent) => {
  if ((event.target as Element | null)?.closest('a')) close()
}

const trapFocus = (event: KeyboardEvent) => {
  if (event.key !== 'Tab' || !dialog.value) return

  const focusable = Array.from(
    dialog.value.querySelectorAll<HTMLElement>(focusableSelector),
  )
  if (!focusable.length) {
    event.preventDefault()
    dialog.value.focus()
    return
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement

  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(isOpen, (open) => {
  if (open || !bodyLocked.value) return

  if (import.meta.client) {
    releaseBodyScrollLock()
    if (previousActiveElement.value?.isConnected) {
      previousActiveElement.value.focus()
    }
  }
  bodyLocked.value = false
})

watch(hasContent, (available) => {
  if (!available) close()
})

onBeforeUnmount(() => {
  if (bodyLocked.value) {
    releaseBodyScrollLock()
    bodyLocked.value = false
  }
})
</script>

<style lang="scss" module>
.root {
  position: relative;
}

.visuallyHidden {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.trigger {
  align-items: center;
  background: var(--purple, #504e8b);
  border: 0;
  border-radius: 0 12px 12px 0;
  box-shadow: 0 3px 12px rgb(0 0 0 / 18%);
  color: var(--white, #fff);
  cursor: pointer;
  display: flex;
  height: 56px;
  justify-content: center;
  padding: 0;
  position: fixed;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 56px;
  z-index: calc(var(--modal-z-index, 1000) - 1);
}

.trigger:hover,
.trigger:focus-visible {
  background: #625da5;
}

.triggerIcon {
  height: 24px;
  width: 24px;
}

.trigger:focus-visible,
.closeButton:focus-visible {
  outline: 3px solid #aaa6ed;
  outline-offset: 3px;
}

.portal {
  inset: 0;
  pointer-events: none;
  position: fixed;
  z-index: var(--modal-z-index, 1000);
}

.portalOpen {
  pointer-events: auto;
}

.backdrop {
  background: rgb(0 0 0 / 60%);
  inset: 0;
  position: absolute;
}

.drawer {
  background: var(--white, #fff);
  box-shadow: 8px 0 28px rgb(0 0 0 / 20%);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 420px;
  position: absolute;
  left: 0;
  top: 0;
  width: min(420px, 92vw);
}

.drawerEnterActive,
.drawerLeaveActive {
  transition: transform 240ms ease;
  will-change: transform;
}

.drawerEnterFrom,
.drawerLeaveTo {
  transform: translateX(-100%);
}

.drawerEnterTo,
.drawerLeaveFrom {
  transform: translateX(0);
}

.header {
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex: 0 0 auto;
  justify-content: space-between;
  padding: 22px 24px 18px;
}

.heading {
  color: #484848;
  font-family: var(--roboto-condensed);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1.2;
  margin: 0;
  text-transform: uppercase;
}

.closeButton {
  align-items: center;
  background: transparent;
  border: 0;
  color: #504e8b;
  cursor: pointer;
  display: flex;
  justify-content: center;
  margin: -8px -8px -8px 12px;
  padding: 8px;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 18px 28px;
}

.journey {
  flex: 0 0 auto;
  margin: 0;
  max-width: none;
}

@media (max-width: 480px) {
  .trigger {
    height: 48px;
    width: 48px;
  }

  .triggerIcon {
    height: 20px;
    width: 20px;
  }

  .header {
    padding-inline: 18px;
  }

  .body {
    padding-inline: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .drawerEnterActive,
  .drawerLeaveActive {
    transition-duration: 0ms;
  }
}
</style>
